import { Component, Input, OnChanges, SimpleChanges, signal, inject, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { BarrierCommandDto, BarrierCommandDtoPaginationResult, BarrierService, BarrierCommandRequest, ParkingService } from '../../../../../../api/gen';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-zone-barrier-commands',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatProgressBarModule, MatProgressSpinnerModule, MatTableModule, MatChipsModule, MatTooltipModule, MatSnackBarModule, MatDividerModule, MatPaginatorModule],
  templateUrl: './zone-barrier-commands.component.html',
  styleUrl: './zone-barrier-commands.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ZoneBarrierCommandsComponent implements OnChanges {
  @Input() barrierId: string | null = null;

  private barrierService = inject(BarrierService);
  private parkingService = inject(ParkingService);
  private snackBar = inject(MatSnackBar);

  private operatorNameCache = new Map<string, string>();

  commands = signal<BarrierCommandDto[]>([]);
  isLoading = signal(false);
  isSendingOpen = signal(false);
  isSendingClose = signal(false);
  isSendingPulse = signal(false);
  displayedColumns: string[] = ['commandType', 'status', 'licensePlate', 'triggeredBy', 'commandSentAt', 'duration'];

  commandsExpanded = signal(false);
  private commandsLoaded = signal(false);

  // Pagination
  pageSize = signal(10);
  pageIndex = signal(0);
  totalCommands = signal(0);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['barrierId']) {
      // Reset state when barrier changes
      this.pageIndex.set(0);
      this.commands.set([]);
      this.totalCommands.set(0);
      this.commandsLoaded.set(false);
      if (this.commandsExpanded()) {
        this.loadCommands();
      }
    }
  }

  toggleCommandsSection(): void {
    const expanding = !this.commandsExpanded();
    this.commandsExpanded.set(expanding);
    if (expanding && !this.commandsLoaded()) {
      this.commandsLoaded.set(true);
      this.loadCommands();
    }
  }

  async loadCommands(): Promise<void> {
    if (!this.barrierId) {
      this.commands.set([]);
      return;
    }

    this.isLoading.set(true);
    try {
      const result = await firstValueFrom(
        this.barrierService.barriersBarrierIdCommandsGet(this.barrierId, this.pageIndex() + 1, this.pageSize())
      ) as BarrierCommandDtoPaginationResult;
      const items = result?.items || [];
      this.commands.set(items);
      this.totalCommands.set(result?.totalCount || 0);
      await this.resolveOperatorNames(items);
    } catch (error) {
      console.error('Error loading barrier commands:', error);
      this.commands.set([]);
      this.totalCommands.set(0);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async resolveOperatorNames(commands: BarrierCommandDto[]): Promise<void> {
    const ids = [...new Set(
      commands
        .map(c => c.createdByOperator)
        .filter((id): id is string => !!id && !this.operatorNameCache.has(id))
    )];
    await Promise.all(
      ids.map(async id => {
        try {
          const op = await firstValueFrom(this.parkingService.parkingOperatorParkingOperatorIdGet(id));
          const name = [op.firstName, op.lastName].filter(Boolean).join(' ') || op.email || id;
          this.operatorNameCache.set(id, name);
        } catch {
          this.operatorNameCache.set(id, id);
        }
      })
    );
  }

  getCreatedBy(command: BarrierCommandDto): string {
    if (command.createdByDetection) return 'By Detection';
    if (command.createdByOperator) {
      return this.operatorNameCache.get(command.createdByOperator) ?? command.createdByOperator;
    }
    return command.triggeredBy || '-';
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.loadCommands();
  }

  getStatusColor(status: string | undefined): string {
    if (!status) return 'default';
    const s = status.toLowerCase();
    if (s.includes('success') || s.includes('completed')) return 'primary';
    if (s.includes('error') || s.includes('failed')) return 'warn';
    if (s.includes('pending') || s.includes('processing')) return 'accent';
    return 'default';
  }

  async sendOpenCommand(): Promise<void> {
    if (!this.barrierId) {
      this.snackBar.open('No barrier selected', 'Close', { duration: 3000 });
      return;
    }

    this.isSendingOpen.set(true);
    try {
      const request: BarrierCommandRequest = {
        triggeredBy: 'Manual Control',
        additionalContext: 'Operator: Keep gate open until manually closed'
      };
      const result = await firstValueFrom(
        this.barrierService.barriersBarrierIdOpenPost(this.barrierId, request)
      );
      this.snackBar.open('✓ Gate will remain open until manually closed', 'Close', { duration: 4000 });
      // Refresh command list after short delay
      setTimeout(() => this.loadCommands(), 500);
    } catch (error) {
      console.error('Error sending open command:', error);
      this.snackBar.open('Failed to send keep open command', 'Close', { duration: 5000 });
    } finally {
      this.isSendingOpen.set(false);
    }
  }

  async sendCloseCommand(): Promise<void> {
    if (!this.barrierId) {
      this.snackBar.open('No barrier selected', 'Close', { duration: 3000 });
      return;
    }

    this.isSendingClose.set(true);
    try {
      const request: BarrierCommandRequest = {
        triggeredBy: 'Manual Control',
        additionalContext: 'Operator: Close gate and clear open commands'
      };
      const result = await firstValueFrom(
        this.barrierService.barriersBarrierIdClosePost(this.barrierId, request)
      );
      this.snackBar.open('✓ Gate closed and open commands cleared', 'Close', { duration: 4000 });
      // Refresh command list after short delay
      setTimeout(() => this.loadCommands(), 500);
    } catch (error) {
      console.error('Error sending close command:', error);
      this.snackBar.open('Failed to send close command', 'Close', { duration: 5000 });
    } finally {
      this.isSendingClose.set(false);
    }
  }

  async sendPulseCommand(): Promise<void> {
    if (!this.barrierId) {
      this.snackBar.open('No barrier selected', 'Close', { duration: 3000 });
      return;
    }

    this.isSendingPulse.set(true);
    try {
      const request: BarrierCommandRequest = {
        triggeredBy: 'Manual Control',
        additionalContext: 'Operator: Quick open pulse - gate opens momentarily'
      };
      const result = await firstValueFrom(
        this.barrierService.barriersBarrierIdPulsePost(this.barrierId, request)
      );
      this.snackBar.open('✓ Quick open pulse sent - gate will open briefly', 'Close', { duration: 4000 });
      // Refresh command list after short delay
      setTimeout(() => this.loadCommands(), 500);
    } catch (error) {
      console.error('Error sending pulse command:', error);
      this.snackBar.open('Failed to send pulse command', 'Close', { duration: 5000 });
    } finally {
      this.isSendingPulse.set(false);
    }
  }

}
